import { TargetableEntity } from './TargetableEntity';

/**
 * Position snapshot for entity interpolation buffer (Phase 2 networking).
 * Stores a timestamped position/velocity for smooth interpolation between server updates.
 */
interface PositionSnapshot {
  timestamp: number;
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  rotation?: number;
}

/**
 * MovableEntity - Has velocity and can move in space.
 *
 * Extends: TargetableEntity
 *
 * Provides:
 * - Velocity (velocityX, velocityY)
 * - Position interpolation (client-side smoothing)
 * - Server sync (target position updates)
 * - Entity interpolation buffer (Phase 2 networking) - opt-in via useInterpolationBuffer
 *
 * Examples: Drop (moves, no collision damage)
 */
export abstract class MovableEntity extends TargetableEntity {
  /** Current velocity X component (pixels per second) */
  velocityX: number = 0;

  /** Current velocity Y component (pixels per second) */
  velocityY: number = 0;

  /** Target position for interpolation (from server) */
  protected targetX: number = 0;
  protected targetY: number = 0;

  /** Target velocity for prediction */
  protected targetVelocityX: number = 0;
  protected targetVelocityY: number = 0;

  /** Target rotation (radians) for smoothing. Opt-in via useRotationSmoothing. */
  protected targetRotation: number = 0;

  /**
   * Whether to smooth rotation toward targetRotation each frame instead of
   * snapping. Server sends drone heading at the tick rate (~20Hz); snapping it
   * reads as janky during turns, especially when position is rendered behind
   * via the interpolation buffer. Subclasses opt in.
   */
  protected useRotationSmoothing: boolean = false;

  // === ENTITY INTERPOLATION BUFFER (Phase 2 networking) ===
  // Stores past positions for smooth interpolation between server updates.
  // Remote entities (RemotePlayer, Bot) opt-in to this via useInterpolationBuffer.
  // Local player does NOT use this - they use client-side prediction instead.

  /** Position history buffer for interpolation */
  protected positionBuffer: PositionSnapshot[] = [];

  /** Interpolation delay in milliseconds (render behind actual time for smooth interpolation) */
  protected interpolationDelayMs: number = 100;

  /**
   * Adjust interpolation delay based on network conditions.
   * Clamped between 50ms (fast LAN) and 250ms (high latency).
   */
  public setInterpolationDelay(ms: number): void {
    this.interpolationDelayMs = Math.max(50, Math.min(250, ms));
  }

  /** Max buffer size (prune old entries) */
  protected maxBufferSize: number = 10;

  /** Whether to use interpolation buffer (opt-in by subclasses) */
  protected useInterpolationBuffer: boolean = false;

  /**
   * Update position with smooth interpolation toward target.
   * Call this in your update() method.
   *
   * If useInterpolationBuffer is enabled, uses the interpolation buffer for smooth
   * rendering at a fixed delay behind real time (Phase 2 networking).
   * Otherwise, uses simple lerp toward target position.
   *
   * @param delta - Time since last frame in milliseconds
   * @param lerpFactor - Interpolation factor (0-1), default 0.15
   */
  protected updatePosition(delta: number, lerpFactor: number = 0.15): void {
    if (this.useInterpolationBuffer) {
      // Phase 2 networking: Use interpolation buffer
      this.updatePositionFromBuffer();
    } else {
      // Legacy: Simple lerp toward target position
      const factor = Math.min(1, lerpFactor);
      this.x += (this.targetX - this.x) * factor;
      this.y += (this.targetY - this.y) * factor;

      // Update target based on velocity (client-side prediction)
      this.targetX += this.targetVelocityX * (delta / 1000);
      this.targetY += this.targetVelocityY * (delta / 1000);
    }
  }

  /**
   * Smoothly rotate toward targetRotation along the shortest arc.
   * No-op unless useRotationSmoothing is enabled. Call once per frame in update().
   *
   * @param lerpFactor - Fraction of the remaining angle closed per frame (0-1).
   */
  protected updateRotation(lerpFactor: number = 0.3): void {
    if (!this.useRotationSmoothing) return;
    let diff = this.targetRotation - this.rotation;
    // Normalize to [-PI, PI] so we always turn the short way around.
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.rotation += diff * Math.min(1, lerpFactor);
  }

  /**
   * Phase 2 networking: Update position from interpolation buffer.
   * Renders entity at a position interpolated between two known past states,
   * with a fixed delay behind real time for smooth movement.
   */
  private updatePositionFromBuffer(): void {
    // Calculate render time (current time minus interpolation delay)
    const renderTime = Date.now() - this.interpolationDelayMs;

    // Find the two snapshots that bracket our render time
    let before: PositionSnapshot | null = null;
    let after: PositionSnapshot | null = null;

    for (let i = 0; i < this.positionBuffer.length; i++) {
      const snapshot = this.positionBuffer[i];
      if (snapshot.timestamp <= renderTime) {
        before = snapshot;
      } else {
        after = snapshot;
        break;
      }
    }

    if (before && after) {
      // Interpolate between the two bracketing snapshots
      const total = after.timestamp - before.timestamp;
      const progress = total > 0 ? (renderTime - before.timestamp) / total : 0;
      const t = Math.max(0, Math.min(1, progress));

      this.x = before.x + (after.x - before.x) * t;
      this.y = before.y + (after.y - before.y) * t;
      this.velocityX = before.velocityX + (after.velocityX - before.velocityX) * t;
      this.velocityY = before.velocityY + (after.velocityY - before.velocityY) * t;
    } else if (before) {
      // Only have past data - extrapolate from last known position
      const dt = (renderTime - before.timestamp) / 1000;
      this.x = before.x + before.velocityX * dt;
      this.y = before.y + before.velocityY * dt;
      this.velocityX = before.velocityX;
      this.velocityY = before.velocityY;
    } else if (after) {
      // Only have future data - use it directly (shouldn't normally happen)
      this.x = after.x;
      this.y = after.y;
      this.velocityX = after.velocityX;
      this.velocityY = after.velocityY;
    }
    // If no data at all, keep current position
  }

  /**
   * Sync position and velocity from server.
   * Called when receiving network updates.
   *
   * If useInterpolationBuffer is enabled, adds snapshot to buffer for interpolation.
   * Otherwise, sets target position for simple lerp.
   */
  syncFromServer(x: number, y: number, velocityX?: number, velocityY?: number): void {
    const vx = velocityX ?? 0;
    const vy = velocityY ?? 0;

    if (this.useInterpolationBuffer) {
      // Phase 2 networking: Add to interpolation buffer
      this.positionBuffer.push({
        timestamp: Date.now(),
        x,
        y,
        velocityX: vx,
        velocityY: vy,
      });

      // Prune old entries (keep only maxBufferSize)
      while (this.positionBuffer.length > this.maxBufferSize) {
        this.positionBuffer.shift();
      }
    } else {
      // Legacy: Direct target update
      this.targetX = x;
      this.targetY = y;
    }

    // Always update velocity
    if (velocityX !== undefined) {
      this.targetVelocityX = velocityX;
      this.velocityX = velocityX;
    }
    if (velocityY !== undefined) {
      this.targetVelocityY = velocityY;
      this.velocityY = velocityY;
    }
  }

  /**
   * Clear the interpolation buffer (call when entity teleports or respawns).
   */
  protected clearInterpolationBuffer(): void {
    this.positionBuffer = [];
  }

  /**
   * Get current velocity as a vector.
   */
  getVelocity(): { x: number; y: number } {
    return { x: this.velocityX, y: this.velocityY };
  }

  /**
   * Get current position as a vector.
   */
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /**
   * Stop all movement immediately.
   * Zeros velocity, target velocity, and syncs target position to current position.
   * Use this for docking, mining, or any mechanic that needs to halt the entity.
   */
  stop(): void {
    this.velocityX = 0;
    this.velocityY = 0;
    this.targetVelocityX = 0;
    this.targetVelocityY = 0;
    // Sync target to current position to prevent interpolation drift
    this.targetX = this.x;
    this.targetY = this.y;
  }
}
